import { Component, OnInit, ViewChild, HostListener, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DataTableComponent } from '../../shared/components/data-table/data-table';
import { TableFiltersComponent } from '../../shared/components/table-filters/table-filters';
import { ViewAllButton } from '../../shared/components/buttons/view-all-button/view-all-button';
import { TableColumn, TableConfig, TableFilter, FilterChangeEvent } from '../../shared/models/table.interface';
import { Transaction, User, Product } from '../../shared/models/data.interface';
import { TransactionDetailsModalComponent, TransactionDetails } from '../../shared/components/modals/transaction-details-modal/transaction-details-modal';

import { FiDataService } from '../../core/services/fi-data.service';
import { AccountAggregatorService } from '../../core/services/account-aggregator.service';
import { NotificationService } from '../../shared/services/notification.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [CommonModule, DataTableComponent, TableFiltersComponent, ViewAllButton, TransactionDetailsModalComponent],
  templateUrl: './transactions.html',
  styleUrls: ['./transactions.scss']
})
export class TransactionsComponent implements OnInit, OnDestroy {
  @ViewChild('transactionTable') transactionTable!: DataTableComponent<Transaction>;
  @ViewChild('userTable') userTable!: DataTableComponent<User>;

  //  Responsive properties
  isMobile = false;
  isTablet = false;

  // Modal state
  isModalOpen = false;
  selectedTransaction: TransactionDetails | null = null;

  // Options popup state
  selectedTransactionId: string | null = null;
  popupPosition = { top: 0, left: 0 };

  // Download modal state
  isDownloadModalOpen = false;
  transactionToDownload: Transaction | null = null;
  downloadLoading = false;

  // Transactions
  transactionColumns: TableColumn<Transaction>[] = [];
  transactionData: any[] = [];
  accounts: any[] = [];
  loading = false;
  originalTransactionData: Transaction[] = []; // Store original unfiltered data
  transactionConfig: TableConfig = {
    searchable: false,
    sortable: true,
    paginated: true,
    itemsPerPage: 10,
    hover: true
  };
  transactionFilters: TableFilter[] = [];
  activeTransactionFilters: { [key: string]: any } = {}; // Track active filters
  private subs = new Subscription();

  // Users
  userColumns: TableColumn<User>[] = [];
  userData: User[] = [];
  originalUserData: User[] = [];
  userConfig: TableConfig = {
    searchable: false,
    sortable: true,
    paginated: true,
    itemsPerPage: 5,
    striped: true,
    hover: true
  };
  userFilters: TableFilter[] = [];
  activeUserFilters: { [key: string]: any } = {};

  // Sort configuration
  sortConfig: { column: string | null, direction: 'asc' | 'desc' } = { column: null, direction: 'asc' };

  // Mobile filters state
  showMobileFilters = false;
  private consentId: string = '';

  constructor(
    private fiDataService: FiDataService,
    private location: Location,
    private aaService: AccountAggregatorService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private route: ActivatedRoute
  ) { }

  goBack(): void {
    this.location.back();
  }

  ngOnInit(): void {
    const preselectedAccount = this.route.snapshot.queryParamMap.get('account');
    if (preselectedAccount) {
      this.activeTransactionFilters['allcards'] = preselectedAccount;
    }

    this.consentId = localStorage.getItem('active_consent_id') || '0ce69719-06c2-4798-a4bd-03f3299c0a1c';
    this.checkDeviceWidth();
    this.initializeTransactionTable();

    // Set up global function for showing options popup from table
    (window as any).showTransactionOptions = (id: string, event: MouseEvent) => {
      this.showOptionsPopup(id, event);
    };
  }

  ngOnDestroy(): void {
    // Cleanup global function
    delete (window as any).showTransactionOptions;
    // Unsubscribe from all subscriptions
    this.subs.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkDeviceWidth();
  }

  private checkDeviceWidth(): void {
    const width = window.innerWidth;
    this.isMobile = width < 768; // Mobile: < 768px
    this.isTablet = width >= 768 && width < 1024; // Tablet: 768px - 1024px
  }

  // ========================================
  // TRANSACTIONS TABLE
  // ========================================
  initializeTransactionTable(): void {
    this.transactionColumns = [
      {
        key: 'name',
        label: 'NAME',
        class: 'col-name',
        render: (value, row) => {
          const iconClass = row.type === 'withdrawal' ? 'icon-up' : 'icon-down';
          const icon = row.type === 'withdrawal' ? 'arrow-up.svg' : 'arrow-down.svg';
          return `
            <div class="d-flex align-items-center" style="width: 100%;">
              <span class="transaction-icon ${iconClass} me-2" style="flex-shrink: 0;">
                <img src="assets/icons/${icon}" alt="${row.type}" class="transaction-arrow-icon">
              </span>
              <span class="transaction-name-text" title="${value}">${value}</span>
            </div>
          `;
        }
      },
      {
        key: 'timestamp',
        label: 'DATE',
        class: 'col-date',
        render: (_, row) => row.date
      },
      {
        key: 'status',
        label: 'STATUS',
        class: 'col-status',
        render: (value) => {
          const statusMap: any = {
            'SUCCESS': {
              icon: 'assets/icons/tick.svg',
              style: 'success'
            },
            'FAILED': {
              icon: 'assets/icons/remove.svg',
              style: 'failed'
            },
            'PROCESS': {
              icon: 'assets/icons/information-polygon.svg',
              style: 'process'
            },
            'PENDING': {
              icon: 'assets/icons/more-vertical.svg',
              style: 'pending'
            }
          };
          const status = statusMap[value] || statusMap['PENDING'];
          return `
            <div class="status-badge-wrapper status-badge-${status.style}">
              <div class="icon-container">
                <div class="icon-overflow">
                  <img src="${status.icon}" alt="${value}" class="status-icon">
                </div>
              </div>
              <div class="status-label">${value}</div>
            </div>
          `;
        }
      },
      {
        key: 'category',
        label: 'CATEGORY',
        class: 'col-category',
        render: (value) => {
          const iconMap: any = {
            'OVERALL BUDGET': 'wallet2',
            'ENTERTAINMENT': 'film',
            'FOOD': 'cup-straw',
            'INCOME': 'cash-stack',
            'UTILITIES': 'lightning',
            'HEALTHCARE': 'heart-pulse',
            'TRANSPORT': 'car-front',
            'REFUND': 'arrow-counterclockwise'
          };
          const icon = iconMap[value] || 'tag';
          return `
            <div class="status-badge-wrapper status-badge-edit">
              <div class="icon-container">
                <div class="icon-overflow">
                  <img src="assets/icons/pencil.svg" alt="${value}" class="status-icon">
                </div>
              </div>
              <div class="status-label">${value}</div>
            </div>
          `;/*<i class="bi bi-${icon}"></i>*/
        }
      },
      {
        key: 'amount',
        label: 'AMOUNT',
        class: 'col-amount',
        render: (value) => {
          const isPositive = value >= 0;
          const colorClass = isPositive ? 'amount-positive' : 'amount-negative';
          const sign = isPositive ? '+' : '';
          const absValue = Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          return `<span class="${colorClass}">${sign}₹${absValue}</span>`;
        }
      },
      {
        key: 'balance',
        label: 'BALANCE',
        class: 'col-balance',
        sortable: false,
        render: (value, row) => {
          const isEncircled = ['FAILED', 'PROCESS'].includes(row.status);
          const encircledClass = isEncircled ? 'encircled-icon' : '';

          return `
          <div data-view="No" class="balance-cloumn-div" style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 16px; height: 16px; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center;">
              <img src="assets/icons/view.svg" alt="View" class="eye-icon" style="width: 100%; height: 100%; object-fit: contain;">
            </div>
            <div class="hide-view-currency" style="white-space: nowrap;">₹****</div>
            <div class="more-vertical-icon ${encircledClass}" onclick="window.showTransactionOptions('${row.id}', event)" style="position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; cursor: pointer;">
              <img src="assets/icons/more-vertical.svg" alt="More" style="width: 100%; height: 100%; object-fit: contain;">
            </div>
          </div>
        `;
        }
      }
    ];

    this.transactionFilters = [
      {
        type: 'daterange',
        key: 'date',
        placeholder: 'Select Date Range'
      },
      {
        type: 'dropdown',
        key: 'allcards',
        placeholder: 'All Cards',
        options: [] // Will be populated dynamically from API
      },
      {
        type: 'dropdown',
        key: 'status',
        placeholder: 'Status',
        options: [
          { value: 'SUCCESS', label: 'Success' },
          { value: 'FAILED', label: 'Failed' },
          { value: 'PROCESS', label: 'Processing' },
          { value: 'PENDING', label: 'Pending' }
        ]
      },
      {
        type: 'dropdown',
        key: 'category',
        placeholder: 'All Categories',
        options: [
          { value: 'OVERALL BUDGET', label: 'Overall Budget' },
          { value: 'INCOME', label: 'Income' },
          { value: 'FOOD', label: 'Food' },
          { value: 'TRANSPORT', label: 'Transport' },
          { value: 'ENTERTAINMENT', label: 'Entertainment' },
          { value: 'HEALTHCARE', label: 'Healthcare' },
          { value: 'UTILITIES', label: 'Utilities' },
          { value: 'REFUND', label: 'Refund' }
        ]
      },
      {
        type: 'search',
        key: 'search',
        placeholder: 'Search'
      }
    ];

    this.fetchData();
  }
  fetchData() {
    this.loading = true;

    this.subs.add(this.fiDataService.getAllFIData(this.consentId, 'cache')
      .subscribe({
        next: (response) => {
          this.accounts = this.aaService.parseAccountAggregatorResponse(response);

          // Transform all transactions from all accounts (preserves account association)
          this.transactionData = this.aaService.transformAllAccountsTransactions(this.accounts);
          this.originalTransactionData = [...this.transactionData]; // Store a copy of original data


          // Populate card dropdown with unique account numbers
          const cardOptions = this.aaService.getUniqueAccountNumbers(this.accounts);
          const cardFilterIndex = this.transactionFilters.findIndex(f => f.key === 'allcards');
          if (cardFilterIndex !== -1) {
            this.transactionFilters[cardFilterIndex].options = cardOptions;
          }

          this.applyActiveFilters(); // Apply filters based on initial route query params

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.cdr.detectChanges();
        }
      }));
  }

  onTransactionFilterChange(event: FilterChangeEvent): void {
    // Update the active filters
    this.activeTransactionFilters[event.filterKey] = event.filterValue;
    this.applyActiveFilters();
  }

  private applyActiveFilters(): void {
    // Start with the original data
    let filtered = [...this.originalTransactionData];

    // Apply each active filter
    Object.keys(this.activeTransactionFilters).forEach(filterKey => {
      const filterValue = this.activeTransactionFilters[filterKey];

      // Skip empty filter values
      if (!filterValue || filterValue === '') {
        return;
      }

      // Apply different filters based on the filter key
      if (filterKey === 'status') {
        filtered = filtered.filter(transaction =>
          transaction.status === filterValue
        );
      } else if (filterKey === 'category') {
        filtered = filtered.filter(transaction =>
          transaction.category === filterValue
        );
      } else if (filterKey === 'search') {
        // Apply search filter across multiple fields
        const searchTerm = filterValue.toLowerCase();
        filtered = filtered.filter(transaction =>
          transaction.name.toLowerCase().includes(searchTerm) ||
          transaction.status.toLowerCase().includes(searchTerm) ||
          transaction.category.toLowerCase().includes(searchTerm) ||
          transaction.amount.toString().includes(searchTerm)
        );
      } else if (filterKey === 'allcards') {
        // Filter by last 4 digits of card number
        filtered = filtered.filter(transaction =>
          transaction.cardNumber && transaction.cardNumber.includes(filterValue)
        );
      } else if (filterKey === 'date') {
        // Date range filtering
        if (filterValue && filterValue.start && filterValue.end) {
          const startDate = new Date(filterValue.start);
          const endDate = new Date(filterValue.end);

          // Set time to start and end of day for proper comparison
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);

          filtered = filtered.filter(transaction => {
            if (transaction.timestamp) {
              const transactionDate = new Date(transaction.timestamp);
              const isMatch = transactionDate >= startDate && transactionDate <= endDate;

              return isMatch;
            }
            return false;
          });
        }
      }
      // Add more filter conditions as needed for other filters
    });

    // Update the transaction data with filtered results
    this.transactionData = filtered;
  }

  /**
   * Handle sort change from mobile component
   */
  onMobileSortChange(newSortConfig: { column: string | null, direction: 'asc' | 'desc' }): void {
    this.sortConfig = newSortConfig;

    if (!this.sortConfig.column) {
      return;
    }

    // Sort the transaction data
    this.transactionData = [...this.transactionData].sort((a, b) => {
      const key = this.sortConfig.column as keyof Transaction;
      let aValue: any = a[key];
      let bValue: any = b[key];

      if (aValue === undefined || bValue === undefined) {
        return 0;
      }

      // Handle string comparisons (convert to lowercase for case-insensitive sorting)
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      let comparison = 0;
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return this.sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }

  // ========================================
  // MODAL METHODS
  // ========================================
  showOptionsPopup(id: string, event: MouseEvent): void {
    this.selectedTransactionId = id;

    // Calculate popup position near the clicked element
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Position the popup below and slightly to the left of the icon
    this.popupPosition = {
      top: rect.bottom + 5, // Below the icon with small gap
      left: rect.right - 220 // Align to the right edge of icon
    };
  }

  closeOptionsPopup(): void {
    this.selectedTransactionId = null;
  }

  openTransactionDetailsModal(): void {
    if (this.selectedTransactionId) {
      const transaction = this.transactionData.find(t => t.id?.toString() === this.selectedTransactionId);
      if (transaction) {
        this.openModal(transaction);
        this.closeOptionsPopup();
      }
    }
  }

  downloadStatement(): void {
    if (!this.selectedTransactionId) {
      this.closeOptionsPopup();
      return;
    }

    // Find the transaction
    const transaction = this.transactionData.find(t => t.id?.toString() === this.selectedTransactionId);
    if (!transaction) {
      console.error('Transaction not found');
      this.closeOptionsPopup();
      return;
    }

    this.transactionToDownload = transaction;
    this.isDownloadModalOpen = true;
    this.closeOptionsPopup();
  }

  confirmDownload(): void {
    if (!this.transactionToDownload) {
      return;
    }

    const transaction = this.transactionToDownload;

    // Find the account using the card number (last 4 digits)
    const account = this.accounts.find(acc =>
      acc.maskedAccountNumber && acc.maskedAccountNumber.endsWith(transaction.cardNumber || '')
    );

    if (!account || !account.linkReferenceNumber) {
      console.error('Account or Link Reference Number not found for transaction');
      this.closeDownloadModal();
      return;
    }

    this.downloadLoading = true;
    const linkRefNumber = [account.linkReferenceNumber];

    this.subs.add(this.aaService.downloadStatement(this.consentId, linkRefNumber).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Statement_${transaction.cardNumber}_${new Date().getTime()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Cleanup URL after a short delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        setTimeout(() => {
          this.downloadLoading = false;
          this.closeDownloadModal();
          this.cdr.detectChanges();
        }, 500);
      },
      error: (error) => {
        console.error('Error downloading statement:', error);
        setTimeout(() => {
          this.downloadLoading = false;
          this.closeDownloadModal();
          this.cdr.detectChanges();
        }, 500);
      }
    }));
  }

  closeDownloadModal(): void {
    this.isDownloadModalOpen = false;
    this.transactionToDownload = null;
  }

  openModal(transaction: Transaction): void {
    const dateParts = transaction.date ? transaction.date.split(' ') : [];
    const displayDate = dateParts.length >= 2 ? `${dateParts[0]} ${dateParts[1]}` : transaction.date;
    const displayTime = dateParts.length >= 3 ? dateParts[2] : (dateParts.length > 3 ? dateParts.slice(2).join(' ') : '');

    this.selectedTransaction = {
      id: transaction.id?.toString() || '',
      status: transaction.status,
      amount: Math.abs(transaction.amount).toFixed(2),
      currency: transaction.currency || 'INR',
      date: displayDate,
      time: displayTime,
      identifier: transaction.txnId || transaction.reference || 'N/A',
      name: transaction.name,
      card: transaction.cardNumber || '**** 1234',
      category: transaction.category
    };
    this.isModalOpen = true;
  }

  // ========================================
  // MOBILE FILTERS
  // ========================================

  /**
   * Toggle mobile filters dropdown
   */
  toggleMobileFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selectedTransaction = null;
  }

  toggleNotifications(): void {
    this.notificationService.toggle();
  }
}
